import { Link, useRouter } from "@shared/client/navigation";
import { Fragment, useEffect, useState } from "react";
import { Check, ChevronDown, ChevronRight, ListChecks, ListPlus, LoaderCircle, Plus, Search, Send } from "lucide-react";
import {
  MAX_IMMEDIATE_ANALYSES,
  PROSPECT_STAGE_HINTS,
  PROSPECT_STAGE_LABELS,
  PROSPECT_STAGES,
  PROSPECT_STATUS_LABELS,
  PROSPECT_STATUS_TONES,
  type ProspectBoardItem,
  type ProspectBulkAction,
  type ProspectStage,
} from "@shared/lib/prospects-shared";
import { formatDate, formatNumber } from "@shared/lib/format";
import { cn } from "@shared/lib/utils";
import { AdminChip, AdminEmpty, AdminMeta, AdminPanel } from "@shared/components/blocks";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Checkbox } from "@shared/components/ui/checkbox";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import { Textarea } from "@shared/components/ui/textarea";
import { ReviewWorkspace } from "@/components/admin/prospection/review-view";
import { OrgLogo } from "@shared/components/org-logo";

/**
 * Base de prospection de Hune, rangée par étape de travail : on trie une
 * liste importée (l'agent n'analyse que ce qui est coché), l'agent prépare les
 * dossiers, on complète l'adresse, relit puis valide le premier e-mail, et
 * l'on suit la séquence. Chaque étape a son tableau et ses actions ;
 * la fiche d'un prospect garde le détail.
 */

export type ProspectionStats = {
  contacted: number;
  replied: number;
  positive: number;
  unsubscribed: number;
  bounced: number;
  ready: number;
  toReview: number;
};

export type MachineState = {
  machineEnabled: boolean;
  sendingEnabled: boolean;
  autoApprove: boolean;
  gmailConfigured: boolean;
  activeMailboxes: number;
  usedToday: number;
  dailyQuota: number;
};

export type ProspectBoardData = {
  stage: ProspectStage;
  counts: Record<ProspectStage, number>;
  missingEmail: number;
  unopened: number;
  emailFinder: boolean;
  items: ProspectBoardItem[];
};

const inlineFormClass = "flex flex-wrap items-center gap-3 border-b bg-muted/40 px-4 py-3.5";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Actions groupées proposées dans chaque étape. */
const STAGE_ACTIONS: Record<ProspectStage, Array<{ action: ProspectBulkAction; label: string; primary?: boolean }>> = {
  triage: [
    { action: "queue", label: "Mettre dans la file de l’agent", primary: true },
    { action: "analyze", label: "Analyser maintenant" },
    { action: "dismiss", label: "Écarter" },
    { action: "delete", label: "Supprimer" },
  ],
  queue: [
    { action: "unqueue", label: "Retirer de la file" },
    { action: "analyze", label: "Analyser maintenant" },
    { action: "dismiss", label: "Écarter" },
  ],
  // Un dossier à la fois (ReviewWorkspace) : ses actions sont dans le dossier.
  complete: [],
  ready: [{ action: "dismiss", label: "Écarter et annuler l’envoi" }],
  sequence: [{ action: "dismiss", label: "Écarter et arrêter la séquence" }],
  closed: [],
};

const BULK_DONE: Record<ProspectBulkAction, string> = {
  queue: "mis dans la file de l’agent",
  unqueue: "remis à trier",
  analyze: "en cours d’analyse",
  dismiss: "écarté(s)",
  delete: "supprimé(s)",
};

const EMPTY: Record<ProspectStage, string> = {
  triage: "Rien à trier. Importez une liste de sites : elle arrive ici, et vous cochez ceux que l’agent doit analyser.",
  queue: "La file de l’agent est vide. Cochez des prospects dans « À trier » pour qu’il les prépare.",
  complete:
    "Aucun dossier à vérifier. Les dossiers préparés par l’agent arrivent ici : vous vérifiez la personne, ajoutez son adresse et relisez le premier e-mail.",
  ready: "Aucun e-mail prêt à partir.",
  sequence: "Personne en séquence pour l’instant.",
  closed: "Aucun prospect clos.",
};

async function send(path: string, method: string, body: unknown) {
  const response = await fetch(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) throw new Error(typeof data.error === "string" ? data.error : "Action impossible.");
  return data;
}

function errorMessage(cause: unknown) {
  return cause instanceof Error ? cause.message : "Action impossible.";
}

/**
 * Import d'une liste collée : une ligne par prospect, champs séparés par « ; »
 * ou une tabulation — nom ; site ; interlocuteur ; e-mail.
 */
function parseImport(raw: string) {
  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [first = "", second = "", third = "", fourth = ""] = line
        .split(/[;\t]/)
        .map((part) => part.trim());
      const looksLikeUrl = /^(https?:\/\/|www\.)|\.[a-z]{2,}(\/.*)?$/i.test(first) && !first.includes(" ");
      // Un site seul suffit : le serveur en tire un nom provisoire, que l'agent
      // remplace par le vrai en cherchant l'interlocuteur.
      const name = looksLikeUrl && !second ? "" : first;
      const website = looksLikeUrl && !second ? first : second;
      return {
        name: name.slice(0, 200),
        website: website || undefined,
        contact_name: third || undefined,
        contact_email: fourth || undefined,
      };
    })
    .filter((item) => item.name.length > 0 || Boolean(item.website));
}

function hostOf(website: string | null) {
  if (!website) return null;
  return website.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
}

export function ProspectionView({
  board,
  stats,
  machine,
  search,
}: {
  board: ProspectBoardData;
  stats: ProspectionStats;
  machine: MachineState;
  search: string;
}) {
  const router = useRouter();
  const { stage, items } = board;
  const [mode, setMode] = useState<"none" | "single" | "import" | "prepared">("none");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selection, setSelection] = useState<{ stage: ProspectStage; ids: Set<string> }>({ stage, ids: new Set() });
  const [anchor, setAnchor] = useState<number | null>(null);
  const [busy, setBusy] = useState<ProspectBulkAction | null>(null);

  // La sélection ne vaut que pour l'étape où elle a été faite, et pour les
  // lignes encore affichées.
  const visible = new Set(items.map((item) => item.id));
  const selected = selection.stage === stage ? [...selection.ids].filter((id) => visible.has(id)) : [];
  const selectable = STAGE_ACTIONS[stage].length > 0;
  const running =
    (stage === "queue" && items.some((item) => item.research_status === "running")) ||
    (stage === "complete" && items.some((item) => item.email_lookup?.status === "running"));

  // Une analyse, ou une recherche d'adresse, en cours se suit sans recharger la page.
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => router.refresh(), 15_000);
    return () => window.clearInterval(timer);
  }, [running, router]);

  function navigate(next: { stage?: ProspectStage; search?: string }) {
    const params = new URLSearchParams();
    const nextSearch = next.search ?? search;
    if (nextSearch) params.set("search", nextSearch);
    params.set("stage", next.stage ?? stage);
    router.replace(`/admin/prospection?${params}`);
  }

  function toggle(index: number, shiftKey: boolean) {
    const id = items[index]?.id;
    if (!id) return;
    const ids = new Set(selected);
    const on = !ids.has(id);
    // Maj+clic : toute la plage depuis la dernière case cochée prend le même état.
    const [from, to] = shiftKey && anchor !== null ? [Math.min(anchor, index), Math.max(anchor, index)] : [index, index];
    for (let position = from; position <= to; position += 1) {
      const target = items[position]?.id;
      if (!target) continue;
      if (on) ids.add(target);
      else ids.delete(target);
    }
    setSelection({ stage, ids });
    setAnchor(index);
  }

  function toggleAll() {
    setSelection({ stage, ids: selected.length === items.length ? new Set() : new Set(items.map((item) => item.id)) });
    setAnchor(null);
  }

  async function bulk(action: ProspectBulkAction) {
    if (selected.length === 0) return;
    if (action === "delete" && !window.confirm(`Supprimer ${selected.length} prospect(s) ? Cette action est définitive.`)) return;
    setBusy(action);
    setError("");
    setNotice("");
    try {
      const data = await send("/api/admin/prospection/bulk", "POST", { action, ids: selected });
      const updated = Number(data.updated ?? 0);
      const skipped = selected.length - updated;
      setNotice(
        `${updated} prospect(s) ${BULK_DONE[action]}.` +
          (skipped > 0 ? ` ${skipped} ignoré(s) : l’action ne les concerne pas à cette étape.` : ""),
      );
      setSelection({ stage, ids: new Set() });
      router.refresh();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(null);
    }
  }

  const replyNote =
    stats.contacted > 0
      ? `30 jours : ${stats.contacted} contacté(s), ${stats.replied} réponse(s)`
      : "30 jours : rien d’envoyé";

  return (
    <>
      <StageBand
        board={board}
        machine={machine}
        onSelect={(next) => {
          setNotice("");
          navigate({ stage: next });
        }}
      />

      <AdminMeta
        note={
          <span className="flex flex-wrap gap-2">
            <Button onClick={() => setMode(mode === "prepared" ? "none" : "prepared")} size="sm" type="button" variant="outline">
              <ListChecks /> Importer des dossiers préparés
            </Button>
            <Button onClick={() => setMode(mode === "import" ? "none" : "import")} size="sm" type="button" variant="outline">
              <ListPlus /> Importer une liste
            </Button>
            <Button onClick={() => setMode(mode === "single" ? "none" : "single")} size="sm" type="button" variant="outline">
              <Plus /> Nouveau prospect
            </Button>
          </span>
        }
      >
        <span className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          <AdminChip tone={machine.machineEnabled ? "active" : "neutral"}>
            Agent {machine.machineEnabled ? "actif" : "coupé"} · {machine.usedToday}/{machine.dailyQuota} dossiers sur 24 h
          </AdminChip>
          <AdminChip tone={machine.sendingEnabled && machine.gmailConfigured && machine.activeMailboxes > 0 ? "active" : "warning"}>
            {!machine.sendingEnabled
              ? "Envois coupés"
              : !machine.gmailConfigured
                ? "Envois actifs, compte de service Google absent"
                : machine.activeMailboxes === 0
                  ? "Envois actifs, aucune boîte active"
                  : `Envois actifs · ${machine.activeMailboxes} boîte${machine.activeMailboxes > 1 ? "s" : ""}`}
          </AdminChip>
          <span className="text-[12.5px]">{replyNote}</span>
          <Link className="text-[12.5px] hover:text-foreground" href="/admin/prospection/reglages">Réglages</Link>
        </span>
      </AdminMeta>

      {error && (
        <Alert className="mb-4" role="alert" variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <AdminPanel flush>
        <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
          <form
            className="relative min-w-[220px] flex-1"
            onSubmit={(event) => {
              event.preventDefault();
              navigate({ search: new FormData(event.currentTarget).get("search")?.toString() || "" });
            }}
          >
            <Search aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="h-9 pl-9" defaultValue={search} key={search} name="search" placeholder="Rechercher un prospect, un site, une adresse…" />
          </form>
          {search && (
            <Button onClick={() => navigate({ search: "" })} size="sm" type="button" variant="ghost">
              Effacer la recherche
            </Button>
          )}
        </div>

        {mode === "single" && <SingleForm onDone={() => setMode("none")} onError={setError} />}
        {mode === "import" && (
          <ImportForm
            onDone={(created, queued) => {
              setMode("none");
              setNotice(`${created} prospect(s) importé(s)${queued ? " et mis dans la file de l’agent" : ""}.`);
              navigate({ stage: queued ? "queue" : "triage", search: "" });
            }}
            onError={setError}
          />
        )}

        {mode === "prepared" && (
          <PreparedImportForm
            onDone={(message) => {
              setMode("none");
              setNotice(message);
              navigate({ stage: "complete", search: "" });
            }}
            onError={setError}
          />
        )}

        <StageIntro machine={machine} stage={stage} />

        {selectable && selected.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-b bg-muted/40 px-4 py-2.5">
            <span className="mr-1 text-[13px] font-medium tabular-nums">{selected.length} sélectionné(s)</span>
            {STAGE_ACTIONS[stage].map((item) => {
              const tooMany = item.action === "analyze" && selected.length > MAX_IMMEDIATE_ANALYSES;
              return (
                <Button
                  disabled={busy !== null || tooMany}
                  key={item.action}
                  onClick={() => bulk(item.action)}
                  size="sm"
                  title={tooMany ? `${MAX_IMMEDIATE_ANALYSES} au plus d’un coup : mettez les autres dans la file.` : undefined}
                  type="button"
                  variant={item.primary ? "default" : "outline"}
                >
                  {busy === item.action && <LoaderCircle className="animate-spin" />}
                  {item.label}
                </Button>
              );
            })}
            <Button onClick={() => setSelection({ stage, ids: new Set() })} size="sm" type="button" variant="ghost">
              Désélectionner
            </Button>
          </div>
        )}
        {notice && <p className="m-0 border-b px-4 py-2.5 text-[12.5px] text-muted-foreground" role="status">{notice}</p>}

        {items.length === 0 ? (
          <AdminEmpty>{search ? "Aucun prospect ne correspond à cette recherche dans cette étape." : EMPTY[stage]}</AdminEmpty>
        ) : stage === "complete" ? null : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {selectable && (
                  <TableHead className="w-10 pl-5">
                    <Checkbox
                      aria-label="Tout sélectionner"
                      checked={selected.length === items.length ? true : selected.length > 0 ? "indeterminate" : false}
                      onCheckedChange={toggleAll}
                    />
                  </TableHead>
                )}
                <StageHeaders selectable={selectable} stage={stage} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, index) => (
                <StageRow
                  checked={selected.includes(item.id)}
                  item={item}
                  key={item.id}
                  onError={setError}
                  onToggle={selectable ? (shiftKey) => toggle(index, shiftKey) : null}
                  stage={stage}
                />
              ))}
            </TableBody>
          </Table>
        )}
        {items.length > 0 && board.counts[stage] > items.length && (
          <p className="m-0 border-t px-4 py-2.5 text-[12px] text-muted-foreground">
            {formatNumber(items.length)} affichés sur {formatNumber(board.counts[stage])} : affinez avec la recherche.
          </p>
        )}
      </AdminPanel>

      {stage === "complete" && items.length > 0 && (
        <div className="mt-4">
          <ReviewWorkspace emailFinder={board.emailFinder} items={items} onError={setError} />
        </div>
      )}
    </>
  );
}

/**
 * Les étapes, en bande de mesures : l'effectif de chacune et ce qu'elle
 * attend. Ce sont des lieux — un clic affiche l'étape, il n'agit sur rien.
 */
function StageBand({
  board,
  machine,
  onSelect,
}: {
  board: ProspectBoardData;
  machine: MachineState;
  onSelect: (stage: ProspectStage) => void;
}) {
  function note(stage: ProspectStage) {
    if (stage === "queue" && board.counts.queue > 0) return `${machine.dailyQuota} dossiers par 24 h`;
    if (stage === "complete" && (board.unopened > 0 || board.missingEmail > 0)) {
      return [
        board.unopened > 0 ? `${board.unopened} nouveau${board.unopened > 1 ? "x" : ""}` : null,
        board.missingEmail > 0 ? `${board.missingEmail} sans adresse` : null,
      ]
        .filter(Boolean)
        .join(" · ");
    }
    return PROSPECT_STAGE_HINTS[stage];
  }
  return (
    <nav aria-label="Étapes de la prospection" className="mb-4.5 overflow-hidden rounded-lg border">
      <div className="-mr-px -mb-px grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6">
        {PROSPECT_STAGES.map((stage) => (
          <button
            aria-current={board.stage === stage ? "page" : undefined}
            className={cn(
              "min-w-0 cursor-pointer border-r border-b px-4 py-3.5 text-left transition-colors hover:bg-muted/60 max-sm:px-3 max-sm:py-3",
              board.stage === stage && "bg-muted",
            )}
            key={stage}
            onClick={() => onSelect(stage)}
            type="button"
          >
            <span className={cn("block truncate text-[12.5px] text-muted-foreground", board.stage === stage && "font-medium text-foreground")}>
              {PROSPECT_STAGE_LABELS[stage]}
            </span>
            <strong className="mt-1.5 block text-[22px] leading-tight font-semibold tracking-[-0.02em] tabular-nums max-sm:text-[19px]">
              {formatNumber(board.counts[stage])}
            </strong>
            <small className="mt-0.5 block truncate text-[12px] text-subtle-foreground">{note(stage)}</small>
          </button>
        ))}
      </div>
    </nav>
  );
}

/** Une phrase sous la barre d'outils : ce qui se passe à cette étape, et ce qui bloque. */
function StageIntro({ stage, machine }: { stage: ProspectStage; machine: MachineState }) {
  const text: Record<ProspectStage, string> = {
    triage:
      "Une liste importée attend ici : cochez ce qui vaut une analyse. Maj+clic coche toute une plage. « Analyser maintenant » passe avant la file, hors quota.",
    queue: machine.machineEnabled
      ? `L’agent prépare ${machine.dailyQuota} dossiers par 24 h, l’après-midi et la nuit : interlocuteur, signal et premier e-mail. Ceux dont l’adresse est connue passent devant.`
      : "L’agent est coupé dans les réglages : rien ne sera préparé tant qu’il ne tourne pas. « Analyser maintenant » reste possible.",
    complete:
      "Un dossier à la fois : la personne à contacter, son adresse, le premier e-mail. Validé, il part au prochain créneau. Un signal reste frais dix jours : passé ce délai, l’e-mail est annulé et le dossier repréparé.",
    ready: machine.sendingEnabled
      ? "Ils partent dans la fenêtre d’envoi, une boîte après l’autre. « Retenir » les remet à relire."
      : "Les envois sont coupés dans les réglages : ces e-mails attendent leur réouverture.",
    sequence: "Les relances partent seules dans le même fil tant que personne ne répond. Les réponses se traitent dans l’onglet Réponses.",
    closed: "Séquences terminées, clients, écartés, désinscrits et adresses invalides.",
  };
  return <p className="m-0 border-b px-4 py-2.5 text-[12.5px] text-muted-foreground">{text[stage]}</p>;
}

function StageHeaders({ stage, selectable }: { stage: ProspectStage; selectable: boolean }) {
  const first = cn(!selectable && "pl-5");
  switch (stage) {
    case "triage":
      return (
        <>
          <TableHead className={first}>Prospect</TableHead>
          <TableHead className="max-sm:hidden">Interlocuteur</TableHead>
          <TableHead className="pr-5 max-sm:hidden">Ajouté</TableHead>
        </>
      );
    case "queue":
      return (
        <>
          <TableHead className={first}>Prospect</TableHead>
          <TableHead className="max-sm:hidden">Interlocuteur</TableHead>
          <TableHead className="pr-5">Passage prévu</TableHead>
        </>
      );
    case "complete":
    case "ready":
      return (
        <>
          <TableHead className={first}>Prospect</TableHead>
          <TableHead className="max-sm:hidden">Destinataire</TableHead>
          <TableHead className="max-sm:hidden">Premier e-mail</TableHead>
          <TableHead className="pr-5 text-right max-sm:hidden">Envoi</TableHead>
        </>
      );
    case "sequence":
      return (
        <>
          <TableHead className={first}>Prospect</TableHead>
          <TableHead className="max-sm:hidden">Interlocuteur</TableHead>
          <TableHead className="max-sm:hidden">Où en est-il</TableHead>
          <TableHead className="pr-5">Statut</TableHead>
        </>
      );
    case "closed":
      return (
        <>
          <TableHead className={first}>Prospect</TableHead>
          <TableHead className="pr-5">Issue</TableHead>
        </>
      );
  }
}

function ProspectCell({ item, selectable, detail }: { item: ProspectBoardItem; selectable: boolean; detail?: React.ReactNode }) {
  return (
    <TableCell className={cn("max-w-[300px] align-top", !selectable && "pl-5")}>
      <div className="flex gap-3">
        <OrgLogo className="mt-0.5" name={item.name} size={32} website={item.website} />
        <div className="min-w-0 flex-1">
          <Link className="font-semibold hover:text-foreground" href={`/admin/prospection/${item.id}`}>
            {item.name}
          </Link>
          <small className="block truncate text-[12px] text-muted-foreground">{hostOf(item.website) ?? "Sans site"}</small>
          {detail}
        </div>
      </div>
    </TableCell>
  );
}

function ContactCell({ item, className }: { item: ProspectBoardItem; className?: string }) {
  return (
    <TableCell className={cn("max-w-[240px]", className)}>
      {item.contact_name ? (
        <>
          {item.contact_name}
          <small className="block truncate text-[12px] text-muted-foreground">
            {[item.contact_role, item.contact_email].filter(Boolean).join(" · ") || "Adresse inconnue"}
          </small>
        </>
      ) : item.contact_email ? (
        item.contact_email
      ) : (
        <span className="text-muted-foreground">L’agent le cherchera</span>
      )}
    </TableCell>
  );
}

function StageRow({
  item,
  stage,
  checked,
  onToggle,
  onError,
}: {
  item: ProspectBoardItem;
  stage: ProspectStage;
  checked: boolean;
  onToggle: ((shiftKey: boolean) => void) | null;
  onError: (message: string) => void;
}) {
  const selectable = onToggle !== null;
  const box = selectable && (
    <TableCell className="w-10 pl-5 align-top">
      <Checkbox
        aria-label={`Sélectionner ${item.name}`}
        checked={checked}
        className="mt-0.5"
        onClick={(event) => {
          event.preventDefault();
          onToggle(event.shiftKey);
        }}
      />
    </TableCell>
  );

  if (stage === "complete" || stage === "ready") {
    return <OutreachRow box={box} item={item} onError={onError} selectable={selectable} stage={stage} />;
  }

  return (
    <TableRow data-state={checked ? "selected" : undefined}>
      {box}
      {stage === "triage" && (
        <>
          <ProspectCell
            detail={item.next_label && <small className="block text-[12px] text-muted-foreground">{item.next_label}</small>}
            item={item}
            selectable={selectable}
          />
          <ContactCell className="max-sm:hidden" item={item} />
          <TableCell className="pr-5 whitespace-nowrap text-muted-foreground tabular-nums max-sm:hidden">
            {formatDate(item.created_at)}
          </TableCell>
        </>
      )}
      {stage === "queue" && (
        <>
          <ProspectCell item={item} selectable={selectable} />
          <ContactCell className="max-sm:hidden" item={item} />
          <TableCell className="pr-5 text-muted-foreground">
            {item.research_status === "running" ? (
              <span className="inline-flex items-center gap-2">
                <LoaderCircle className="size-3.5 animate-spin" /> Analyse en cours…
              </span>
            ) : (
              (item.next_label ?? "—")
            )}
          </TableCell>
        </>
      )}
      {stage === "sequence" && (
        <>
          <ProspectCell item={item} selectable={selectable} />
          <ContactCell className="max-sm:hidden" item={item} />
          <TableCell className="text-muted-foreground max-sm:hidden">{item.next_label}</TableCell>
          <TableCell className="pr-5">
            <AdminChip tone={PROSPECT_STATUS_TONES[item.status]}>{PROSPECT_STATUS_LABELS[item.status]}</AdminChip>
          </TableCell>
        </>
      )}
      {stage === "closed" && (
        <>
          <ProspectCell item={item} selectable={selectable} />
          <TableCell className="pr-5">
            <AdminChip tone={PROSPECT_STATUS_TONES[item.status]}>{item.next_label}</AdminChip>
          </TableCell>
        </>
      )}
    </TableRow>
  );
}

/**
 * Une ligne d'« À compléter » ou de « Prêts à partir » : l'adresse se saisit
 * dans la ligne, le premier e-mail se déplie pour être relu, puis se valide.
 */
function OutreachRow({
  item,
  stage,
  box,
  selectable,
  onError,
}: {
  item: ProspectBoardItem;
  stage: "complete" | "ready";
  box: React.ReactNode;
  selectable: boolean;
  onError: (message: string) => void;
}) {
  const router = useRouter();
  const [email, setEmail] = useState(item.contact_email ?? "");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const pending = item.pending;
  const dirty = email.trim() !== (item.contact_email ?? "");
  const validEmail = EMAIL_PATTERN.test(email.trim());
  const columns = selectable ? 5 : 4;
  const disabledReason =
    !pending || pending.kind !== "sequence"
      ? "Aucun premier e-mail prêt : ouvrez la fiche."
      : pending.status === "failed"
        ? "Échec d’envoi : à reprendre depuis la fiche."
        : !validEmail
          ? "Ajoutez une adresse valide d’abord."
          : null;

  async function saveEmail() {
    if (!dirty || (email.trim() && !validEmail)) return;
    setSaving(true);
    onError("");
    try {
      await send(`/api/admin/prospection/${item.id}`, "PATCH", { contact_email: email.trim() || null });
      router.refresh();
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setSaving(false);
    }
  }

  const emailForm = (
    <form
      className="mt-1.5 flex items-center gap-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        void saveEmail();
      }}
    >
      <Input
        aria-invalid={email.trim() !== "" && !validEmail ? true : undefined}
        aria-label={`Adresse e-mail de ${item.contact_name ?? item.name}`}
        className="h-8 max-w-[230px] text-[12.5px]"
        onBlur={() => void saveEmail()}
        onChange={(event) => setEmail(event.target.value)}
        placeholder="prenom@entreprise.fr"
        type="email"
        value={email}
      />
      {saving && <LoaderCircle aria-label="Enregistrement" className="size-3.5 animate-spin text-muted-foreground" />}
    </form>
  );
  const readToggle = pending && (
    <button
      aria-expanded={open}
      className="mt-1 inline-flex cursor-pointer items-center gap-1 text-[12.5px] font-medium text-muted-foreground hover:text-foreground"
      onClick={() => setOpen(!open)}
      type="button"
    >
      {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
      {open ? "Masquer l’e-mail" : "Lire l’e-mail"}
    </button>
  );
  const schedule = (
    <div className="inline-flex flex-wrap items-center justify-end gap-2">
      {stage === "ready" && !disabledReason && pending?.status === "ready" && (
        <SendNowControl disabled={dirty} item={item} onError={onError} />
      )}
      <ValidateControl disabledReason={disabledReason} email={email.trim()} item={item} onError={onError} />
    </div>
  );

  return (
    <Fragment>
      <TableRow className={cn(open && "border-b-0")}>
        {box}
        <ProspectCell
          detail={
            <>
              {item.offer_name && <small className="block truncate text-[12px] text-subtle-foreground">{item.offer_name}</small>}
              {/* Sous sm, la ligne se replie : destinataire, adresse et envoi sous le nom. */}
              <div className="mt-2 grid gap-2 sm:hidden">
                {item.contact_name && <span className="text-[12.5px]">{item.contact_name}</span>}
                {emailForm}
                {stage === "ready" && <small className="text-[12px] text-muted-foreground">{item.next_label}</small>}
                {readToggle}
                <div>{schedule}</div>
              </div>
            </>
          }
          item={item}
          selectable={selectable}
        />
        <TableCell className="max-w-[260px] min-w-[210px] align-top max-sm:hidden">
          <span className="block truncate">{item.contact_name ?? <span className="text-muted-foreground">Destinataire non nommé</span>}</span>
          {item.contact_role && <small className="block truncate text-[12px] text-muted-foreground">{item.contact_role}</small>}
          {emailForm}
        </TableCell>
        <TableCell className="max-w-[320px] align-top max-sm:hidden">
          {item.selected_lead && (
            <span className="block truncate">
              {item.selected_lead.organization}
              {item.selected_lead.priority !== null && (
                <span className="text-muted-foreground tabular-nums"> · {item.selected_lead.priority}/100</span>
              )}
            </span>
          )}
          {pending?.subject && <small className="block truncate text-[12px] text-muted-foreground">« {pending.subject} »</small>}
          {stage === "complete" && item.next_label && (
            <small className={cn("block text-[12px]", pending?.status === "held" || pending?.status === "failed" ? "text-destructive" : "text-muted-foreground")}>
              {item.next_label}
            </small>
          )}
          {readToggle}
        </TableCell>
        <TableCell className="pr-5 text-right align-top max-sm:hidden">
          {stage === "ready" && (
            <small className="mb-1.5 ml-auto block max-w-[240px] text-[12px] whitespace-normal text-muted-foreground">
              {item.next_label}
            </small>
          )}
          {schedule}
        </TableCell>
      </TableRow>
      {open && pending && (
        <TableRow className="hover:bg-transparent">
          <TableCell className="pt-0 pr-5 pb-4 pl-5" colSpan={columns}>
            <div className="rounded-lg border bg-muted/30 px-4 py-3.5 text-[13px]">
              {pending.subject && <p className="m-0 mb-2 font-semibold">{pending.subject}</p>}
              <p className="m-0 whitespace-pre-wrap text-foreground/90">{pending.body}</p>
              <p className="m-0 mt-3 text-[12px] text-muted-foreground">
                La signature est ajoutée à l’envoi.{" "}
                <Link className="font-medium hover:text-foreground" href={`/admin/prospection/${item.id}`}>
                  Modifier dans la fiche
                </Link>
              </p>
            </div>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

/**
 * Valider le premier e-mail (l'adresse saisie s'enregistre d'abord) : il part
 * au prochain créneau de la fenêtre d'envoi. Un e-mail prêt se retient pour
 * être relu de nouveau.
 */
function ValidateControl({
  item,
  email,
  disabledReason,
  onError,
}: {
  item: ProspectBoardItem;
  email: string;
  disabledReason: string | null;
  onError: (message: string) => void;
}) {
  const router = useRouter();
  const pending = item.pending;
  const [busy, setBusy] = useState(false);
  const ready = pending?.status === "ready";

  async function submit() {
    if (!pending) return;
    if (pending.status === "held" && !window.confirm("Le garde-fou a retenu cet e-mail. L’avez-vous relu ? Il partira tel quel.")) return;
    setBusy(true);
    onError("");
    try {
      if (email !== (item.contact_email ?? "")) {
        await send(`/api/admin/prospection/${item.id}`, "PATCH", { contact_email: email });
      }
      await send(`/api/admin/prospection/messages/${pending.id}`, "PATCH", ready ? { action: "hold" } : { action: "approve" });
      router.refresh();
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  if (disabledReason) {
    return (
      <span className="inline-block max-w-[200px] text-[12px] text-muted-foreground">
        {disabledReason.includes("fiche") ? (
          <Link className="hover:text-foreground" href={`/admin/prospection/${item.id}`}>{disabledReason}</Link>
        ) : (
          disabledReason
        )}
      </span>
    );
  }

  return (
    <Button disabled={busy} onClick={() => void submit()} size="sm" type="button" variant={ready ? "outline" : "default"}>
      {busy ? <LoaderCircle className="animate-spin" /> : ready ? null : <Check />}
      {ready ? "Retenir" : "Valider"}
    </Button>
  );
}

/**
 * Faire partir un e-mail prêt sans attendre la fenêtre d'envoi : le worker
 * l'envoie seul, avec les garde-fous de tout envoi. La page se recharge le
 * temps qu'il parte.
 */
function SendNowControl({
  item,
  disabled,
  onError,
}: {
  item: ProspectBoardItem;
  disabled: boolean;
  onError: (message: string) => void;
}) {
  const router = useRouter();
  const [busy, setBusy] = useState(false);

  async function submit() {
    const pending = item.pending;
    if (!pending) return;
    if (!window.confirm(`Envoyer maintenant le premier e-mail à ${item.contact_email ?? item.name} ?`)) return;
    setBusy(true);
    onError("");
    try {
      await send(`/api/admin/prospection/messages/${pending.id}`, "PATCH", { action: "send_now" });
      // L'envoi tient en quelques secondes dans le worker.
      for (const delay of [3000, 5000, 8000]) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        router.refresh();
      }
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button disabled={busy || disabled} onClick={() => void submit()} size="sm" type="button">
      {busy ? <LoaderCircle className="animate-spin" /> : <Send />}
      {busy ? "Envoi…" : "Envoyer maintenant"}
    </Button>
  );
}

function SingleForm({ onDone, onError }: { onDone: () => void; onError: (message: string) => void }) {
  const router = useRouter();
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    onError("");
    try {
      const data = await send("/api/admin/prospection", "POST", {
        name: name || undefined,
        website: website || undefined,
        contact_name: contactName || undefined,
        contact_email: contactEmail || undefined,
      });
      onDone();
      router.push(`/admin/prospection/${(data as { prospect: { id: string } }).prospect.id}`);
      router.refresh();
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className={inlineFormClass} onSubmit={submit}>
      <Input className="h-9 max-w-[220px] bg-background" onChange={(event) => setName(event.target.value)} placeholder="Nom (facultatif avec un site)" required={!website} value={name} />
      <Input className="h-9 max-w-[220px] bg-background" inputMode="url" onChange={(event) => setWebsite(event.target.value)} placeholder="Site web" value={website} />
      <Input className="h-9 max-w-[200px] bg-background" onChange={(event) => setContactName(event.target.value)} placeholder="Interlocuteur (optionnel)" value={contactName} />
      <Input className="h-9 max-w-[220px] bg-background" onChange={(event) => setContactEmail(event.target.value)} placeholder="E-mail (optionnel)" type="email" value={contactEmail} />
      <Button disabled={busy} size="sm" type="submit">
        {busy ? <LoaderCircle className="animate-spin" /> : <Plus />} Ajouter
      </Button>
    </form>
  );
}

function ImportForm({
  onDone,
  onError,
}: {
  onDone: (created: number, queued: boolean) => void;
  onError: (message: string) => void;
}) {
  const [raw, setRaw] = useState("");
  const [queue, setQueue] = useState(false);
  const [busy, setBusy] = useState(false);
  const items = parseImport(raw);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (items.length === 0) return;
    setBusy(true);
    onError("");
    try {
      const data = await send("/api/admin/prospection", "POST", { items: items.slice(0, 500), queue });
      setRaw("");
      onDone(Number(data.created ?? 0), queue);
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="grid gap-3 border-b bg-muted/40 px-4 py-3.5" onSubmit={submit}>
      <Label htmlFor="prospect-import">
        Un site par ligne suffit : l’agent trouve la personne à contacter, le signal et rédige le premier e-mail.
        Sinon : <code className="font-mono">nom ; site ; interlocuteur ; e-mail</code>
      </Label>
      <Textarea
        className="bg-background font-mono text-[12px]"
        id="prospect-import"
        onChange={(event) => setRaw(event.target.value)}
        placeholder={"https://ateliernord.fr\nhttps://www.exemple-conseil.fr\nCabinet Durand ; https://cabinet-durand.fr ; Marie Durand ; marie@cabinet-durand.fr"}
        rows={5}
        value={raw}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Label className="font-normal">
            <Checkbox checked={queue} onCheckedChange={(value) => setQueue(value === true)} />
            Les mettre directement dans la file de l’agent
          </Label>
          <span className="text-[12px] text-muted-foreground">
            {items.length === 0
              ? "Sans cette case, la liste arrive dans « À trier ». Les sites déjà en base sont ignorés."
              : `${items.length} ligne${items.length > 1 ? "s" : ""} reconnue${items.length > 1 ? "s" : ""}${items.length > 500 ? " (500 importées au plus)" : ""} · les doublons sont ignorés.`}
          </span>
        </span>
        <Button disabled={busy || items.length === 0} size="sm" type="submit">
          {busy ? <LoaderCircle className="animate-spin" /> : <ListPlus />} Importer
        </Button>
      </div>
    </form>
  );
}

/**
 * Dossiers préparés hors du moteur (JSON ou CSV, collés ou chargés depuis un
 * fichier) : chacun arrive « à vérifier » avec son interlocuteur, ses
 * signaux, son premier e-mail et ses relances déjà écrites. Format :
 * docs/PROSPECTION_SDR.md.
 */
function PreparedImportForm({ onDone, onError }: { onDone: (message: string) => void; onError: (message: string) => void }) {
  const [raw, setRaw] = useState("");
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);

  async function loadFile(file: File | undefined) {
    if (!file) return;
    setRaw(await file.text());
    if (!source) setSource(file.name.replace(/\.(json|csv|txt)$/i, ""));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!raw.trim()) return;
    setBusy(true);
    onError("");
    try {
      const data = await send("/api/admin/prospection/prepared", "POST", { content: raw, source: source || undefined });
      const created = Number(data.created ?? 0);
      const held = Number(data.held ?? 0);
      const skipped = Number(data.skipped ?? 0);
      const invalid = Array.isArray(data.invalid) ? (data.invalid as Array<{ index: number; error: string }>) : [];
      setRaw("");
      onDone(
        `${created} dossier(s) ajouté(s), à vérifier un à un.` +
          (held > 0 ? ` ${held} premier(s) e-mail(s) retenu(s) par le garde-fou, à reprendre.` : "") +
          (skipped > 0 ? ` ${skipped} déjà dans la base.` : "") +
          (invalid.length > 0
            ? ` ${invalid.length} écarté(s) : ${invalid.slice(0, 3).map((item) => `n° ${item.index} (${item.error})`).join(" ; ")}.`
            : ""),
      );
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="grid gap-3 border-b bg-muted/40 px-4 py-3.5" onSubmit={submit}>
      <Label htmlFor="prepared-import">
        Des dossiers déjà recherchés et rédigés, en JSON ou en CSV : l’entreprise, la personne à contacter, les signaux
        sourcés, le premier e-mail et les relances. Ils arrivent « à vérifier » ; rien ne part sans validation.
      </Label>
      <Textarea
        className="bg-background font-mono text-[12px]"
        id="prepared-import"
        onChange={(event) => setRaw(event.target.value)}
        placeholder={'[{ "name": "…", "website": "https://…", "contact": { "name": "…" }, "signals": [ … ], "emails": { "subject": "…", "first_body": "…" } }]'}
        rows={6}
        value={raw}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="flex flex-wrap items-center gap-3">
          <Input
            accept=".json,.csv,.txt,application/json,text/csv"
            aria-label="Charger un fichier JSON ou CSV"
            className="h-9 w-auto max-w-[260px] bg-background text-[12.5px]"
            onChange={(event) => void loadFile(event.target.files?.[0])}
            type="file"
          />
          <Input
            aria-label="Origine notée sur les fiches"
            className="h-9 w-[240px] bg-background"
            maxLength={200}
            onChange={(event) => setSource(event.target.value)}
            placeholder="Origine (ex. liste Claude, novembre)"
            value={source}
          />
        </span>
        <Button disabled={busy || !raw.trim()} size="sm" type="submit">
          {busy ? <LoaderCircle className="animate-spin" /> : <ListChecks />} Importer les dossiers
        </Button>
      </div>
    </form>
  );
}
